import React from 'react';
import { HardDrive, Cloud, Check } from 'lucide-react';

export const FileDeliverySection = ({ 
  deliveryMethod, 
  hasEditingOptions,
  onChange, 
  formatIDR 
}) => {
  const options = [
    {
      id: 'on_site',
      name: 'On-Site Transfer',
      description: 'Get your raw files directly at the studio via your own USB drive or hard disk',
      price: 0,
      icon: HardDrive,
      features: ['Instant transfer', 'Bring your own drive', 'No waiting time'],
      warning: 'Minimum 100GB storage required per hour of recording'
    },
    {
      id: 'google_drive',
      name: 'Google Drive Delivery',
      description: 'Receive your files via a shared Google Drive link within 24h',
      price: hasEditingOptions ? 0 : 100000,
      icon: Cloud,
      features: ['Convenient access', 'Share with your team', 'Cloud backup included'],
      includedWithEditing: hasEditingOptions
    }
  ];

  return (
    <div className="bg-muted/30 p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-heading font-semibold">Raw Footage Delivery</h3>
          <p className="text-sm text-foreground-muted">How would you like to receive your files?</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = deliveryMethod === option.id;
          
          return (
            <div
              key={option.id}
              onClick={() => onChange(option.id)}
              className={`cursor-pointer p-5 border-2 transition-all duration-300 ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid={`delivery-${option.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  isSelected ? 'bg-primary/20' : 'bg-muted'
                }`}>
                  <Icon className={`w-6 h-6 ${
                    isSelected ? 'text-primary' : 'text-foreground-muted'
                  }`} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold">{option.name}</h4>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-4 h-4 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-foreground-muted mb-3">
                    {option.description}
                  </p>
                  
                  {/* Price */}
                  <div className="flex items-center gap-2 mb-3">
                    {option.includedWithEditing ? (
                      <span className="text-sm font-semibold text-green-400">
                        Included with editing
                      </span>
                    ) : option.price === 0 ? (
                      <span className="text-sm font-semibold text-primary">Free</span>
                    ) : (
                      <span className="text-sm font-semibold text-primary">
                        +{formatIDR(option.price)}
                      </span>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-1">
                    {option.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-foreground-muted">
                        <Check className="w-3 h-3 text-primary" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Warning for USB */}
                  {option.warning && (
                    <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                      ⚠️ {option.warning}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info note */}
      {hasEditingOptions && deliveryMethod === 'google_drive' && (
        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 text-sm">
          <span className="text-green-400 font-semibold">Google Drive delivery is included</span>
          <span className="text-foreground-muted"> because you selected editing options.</span>
        </div>
      )}
    </div>
  );
};
